import { useRouteLoaderData } from '@remix-run/react';
import { Heart } from '@solar-icons/react';
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import IntegrittyLogo from '~/components/icons/IntegrittyLogo';
import PaymentLogoApplePay from '~/components/icons/PaymentLogoApplePay';
import PaymentLogoGooglePay from '~/components/icons/PaymentLogoGooglePay';
import PaymentLogoMastercard from '~/components/icons/PaymentLogoMastercard';
import PaymentLogoPayPal from '~/components/icons/PaymentLogoPayPal';
import PaymentLogoVisa from '~/components/icons/PaymentLogoVisa';
import SocialFacebook from '~/components/icons/SocialFacebook';
import SocialInstagram from '~/components/icons/SocialInstagram';
import SocialPinterest from '~/components/icons/SocialPinterest';
import ContactItem from '~/components/layout/footer/ContactItem';
import LinksNav from '~/components/layout/footer/LinksNav';
import NewsletterForm from '~/components/layout/footer/NewsletterForm';
import ShopLogo from '~/components/layout/header/ShopLogo';
import { useLocale } from '~/hooks/useLocale';
import type { loader as rootLoader } from '~/root';
import { defaultLocale } from '~/utils/language';

interface FooterLinks {
  settings: {
    title: string;
    links: {
      label: string;
      url: string;
    }[];
  }[];
}

interface FooterProps {
  isCheckout?: boolean;
}

/**
 * Grid width per link group, between the contact column (`col-lg-4`) and the newsletter
 * column (`col-lg-3`). Sized for the two groups the API returns today; a third would
 * reuse the last width.
 */
const FOOTER_LINK_COL_SIZES = [3, 2];

const Footer = ({ isCheckout }: FooterProps) => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const locale = useLocale();
  const storeEmail = rootData?.storeEmail || undefined;
  const storePhone = rootData?.storePhone || undefined;
  const footerLinks = (rootData?.footerLinksData as FooterLinks) || [];
  const localePrefix = locale === defaultLocale ? '' : `/${locale}`;

  const shopName = 'Anders & Kafka';

  const now = new Date();
  const currentYear = now.getFullYear();

  return (
    <footer className="mt-auto bg-body text-body" data-bs-theme="dark">
      {!isCheckout && (
        <Container className="footer-main">
          <Row className="align-items-start justify-content-between gy-5">
            <Col xs={12} lg={4}>
              <ShopLogo
                size={{ width: 263, height: 54 }}
                variant="dark"
                showEshopText={false}
                href="https://anderskafka.cz/"
                className="d-inline-block mb-4 footer-logo"
              />
              {storeEmail && <ContactItem type="email" value={storeEmail} className="mb-3" />}
              {storePhone && <ContactItem type="phone" value={storePhone} className="mb-3" />}
              <ContactItem
                type="address"
                value={t('footer.store_address')}
                label={t('footer.store_hours')}
                mapUrl="https://maps.app.goo.gl/oHqrehvNByoS3byD8"
              />
            </Col>
            {footerLinks?.settings?.map((linkGroup, index) => {
              const groups = footerLinks.settings;
              const hasTwoGroups = groups.length === 2;
              // A long list in a two-group footer splits into two link columns
              // (see `twoColumns` in LinksNav), which widens the column on its own.
              const isWide = hasTwoGroups && linkGroup.links.length > 7;

              return (
                <Col
                  xs={12}
                  lg={FOOTER_LINK_COL_SIZES[index] ?? FOOTER_LINK_COL_SIZES.at(-1)}
                  key={index}
                  className="d-flex flex-column gap-4 footer-links-col"
                >
                  <LinksNav
                    title={linkGroup.title}
                    twoColumns={isWide}
                    links={linkGroup.links
                      .filter((link) => link.url && link.label)
                      .map((link) => {
                        const slug =
                          link.url.startsWith('/') || link.url.startsWith('http') ? link.url : `/${link.url}`;
                        return {
                          label: link.label,
                          href: `${localePrefix}${slug}`,
                        };
                      })}
                  />
                  {/* Social block sits under the last group (Kontakt). It comes last in the
                      markup too, so no flex `order` is needed to put it below the links. */}
                  {index === groups.length - 1 && (
                    <div className="footer-social d-flex gap-3 align-items-center align-items-lg-start flex-lg-column">
                      <p className="fw-semibold footer-heading text-uppercase mb-0">{t('footer.follow_us')}</p>
                      <div className="d-flex gap-2">
                        <a
                          href={t('footer.facebook_url')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-social-icon-btn"
                          aria-label="Facebook"
                        >
                          <SocialFacebook />
                        </a>
                        <a
                          href={t('footer.instagram_url')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-social-icon-btn"
                          aria-label="Instagram"
                        >
                          <SocialInstagram />
                        </a>
                        <a
                          href={t('footer.pinterest_url')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-social-icon-btn"
                          aria-label="Pinterest"
                        >
                          <SocialPinterest />
                        </a>
                      </div>
                    </div>
                  )}
                </Col>
              );
            })}
            <Col xs={12} lg={3}>
              <NewsletterForm />
            </Col>
          </Row>
        </Container>
      )}
      <Container className="footer-bottom-bar">
        <Row className="align-items-center gy-4">
          <Col
            xs={12}
            lg={4}
            className="d-flex align-items-center justify-content-center justify-content-lg-start gap-1"
          >
            <span className="payment-method">
              <PaymentLogoVisa />
            </span>
            <span className="payment-method">
              <PaymentLogoMastercard />
            </span>
            <span className="payment-method">
              <PaymentLogoApplePay />
            </span>
            <span className="payment-method">
              <PaymentLogoGooglePay />
            </span>
            <span className="payment-method">
              <PaymentLogoPayPal />
            </span>
          </Col>
          <Col xs={12} lg={4}>
            <p className="mb-0 text-center text-muted">{t('footer.copyright', { year: currentYear, shopName })}</p>
          </Col>
          <Col xs={12} lg={4} className="text-center text-lg-end">
            <a
              href="https://www.integritty.cz/"
              target="_blank"
              className="d-inline-flex align-items-center gap-1 text-muted text-decoration-none made-by-integritty"
            >
              <span>{t('footer.made_with')}</span>
              <Heart weight="Linear" size={24} className="heart-icon" aria-label={t('footer.love')} />
              <IntegrittyLogo />
            </a>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export default Footer;
